import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, QuestionModal, Spinner } from '../../../../../common/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import {
  useDeleteEventMutation,
  useGetArchivedEventsQuery,
  useGetNonArchivedEventsQuery,
  useSaveArchivationMutation,
} from '../../../../../../redux/services/fo_events.jsx';
import EventsTable from '../../../../../common/FriOpen/Events/EventsTable/EventsTable.jsx';
import {
  ROLE_FRI_OPEN_ADMIN,
  ROLE_FRI_OPEN_LECTURER,
} from '../../../../../../utils/constants/roles.js';
import useUserHasRole from '../../../../../../hooks/useUserHasRoles.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';
import TableFilter from '../../../../../common/TableFilter/TableFilter.jsx';

const TABLE_FILTER_TITLE = 'Filter akcií';

const EventsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { saveSuccess: showSaveSuccess, deleteSuccess: showDeleteSuccess } =
    useToastMessages();

  const isAdmin = useUserHasRole(ROLE_FRI_OPEN_ADMIN);
  const isFriOpenLecturer = useUserHasRole(ROLE_FRI_OPEN_LECTURER);
  const canViewArchived = isAdmin || isFriOpenLecturer;
  const [selectedListOption, setSelectedListOption] = useState(
    () => localStorage.getItem(TABLE_FILTER_TITLE) || 'active',
  );
  const [eventToArchive, setEventToArchive] = useState(null);

  const {
    data: events,
    isLoading,
    refetch: refetchEvents,
  } = useGetNonArchivedEventsQuery(isAdmin);

  const {
    data: archivedEvents,
    isLoading: isLoadingArchived,
    refetch: refetchArchivedEvents,
  } = useGetArchivedEventsQuery(undefined, {
    skip: !canViewArchived,
  });

  const [archiveEvent, { isSuccess: isSuccessArchive, reset: resetArchiveEvent }] =
    useSaveArchivationMutation();

  const [deleteEvent, { isSuccess: isSuccessDelete, reset: resetDeleteEvent }] =
    useDeleteEventMutation();

  useEffect(() => {
    if (!canViewArchived && selectedListOption === 'archived') {
      localStorage.setItem(TABLE_FILTER_TITLE, 'active');
      setSelectedListOption('active');
    }
  }, [canViewArchived, selectedListOption]);

  useEffect(() => {
    if (!isSuccessArchive) {
      return;
    }

    showSaveSuccess();
    refetchEvents();
    if (canViewArchived) {
      refetchArchivedEvents();
    }
    resetArchiveEvent();
  }, [isSuccessArchive]);

  useEffect(() => {
    if (!isSuccessDelete) {
      return;
    }

    showDeleteSuccess();
    refetchEvents();
    if (canViewArchived) {
      refetchArchivedEvents();
    }
    resetDeleteEvent();
  }, [isSuccessDelete]);

  const currentEvents = useMemo(() => {
    if (canViewArchived && selectedListOption === 'archived') {
      return archivedEvents;
    }

    return events;
  }, [archivedEvents, canViewArchived, events, selectedListOption]);

  const currentIsLoading = useMemo(() => {
    if (canViewArchived && selectedListOption === 'archived') {
      return isLoadingArchived;
    }

    return isLoading;
  }, [canViewArchived, isLoading, isLoadingArchived, selectedListOption]);

  const currentIsArchived = canViewArchived && selectedListOption === 'archived';

  const pageTitle = currentIsArchived
    ? t('Archivované FRI Akcie')
    : t('FRI Akcie');

  const collapseTitle = currentIsArchived
    ? t('Archivované FRI Akcie')
    : t('Akcie FRI');

  return currentIsLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
        createText={t('Pridať FRI akciu')}
        createUrl={routes.cmsFoEventsCreate}
        permissionsBasic={'fo-events'}
      />
      <div style={{ margin: '10px' }}>
        {canViewArchived ? (
          <TableFilter
            title={t(TABLE_FILTER_TITLE)}
            value={selectedListOption}
            setValue={setSelectedListOption}
            options={[
              { value: 'active', label: t('Aktívne akcie') },
              { value: 'archived', label: t('Archivované akcie') },
            ]}
          />
        ) : null}

        <CollapseBlock title={collapseTitle}>
          <EventsTable
            events={currentEvents}
            onDelete={(event) => deleteEvent(event.id)}
            onEditClick={
              currentIsArchived
                ? undefined
                : (event) => navigate(routes.cmsFoEventsEdit(event.id))
            }
            onRowClick={
              isAdmin && !currentIsArchived
                ? (event) => navigate(routes.cmsFoEventsEdit(event.id))
                : (event) => navigate(routes.cmsFoEventsDetail(event.id))
            }
            onDetailClick={(event) =>
              navigate(routes.cmsFoEventsDetail(event.id))
            }
            onShowWorkshopsClick={(event) =>
              navigate(routes.cmsFoWorkshopsList(event.id))
            }
            onArchiveClick={(event) =>
              currentIsArchived
                ? navigate(routes.cmsFoEventsUnarchive(event.id))
                : setEventToArchive(event)
            }
            isAdmin={isAdmin}
            isArchived={currentIsArchived}
          />
        </CollapseBlock>

        <QuestionModal
          chosenPositive={() => {
            archiveEvent({ id: eventToArchive.id });
            setEventToArchive(null);
          }}
          question={
          t(`Naozaj chcete archivovať FRI.open akciu? Spolu s ňou budú archivované aj všetky semináre a termíny.`)
         }
          visible={eventToArchive !== null}
          yes={t('Áno')}
          toggle={() => setEventToArchive(null)}
          headerTitle={t('Archivovať akciu')}
        />
      </div>
    </>
  );
};

export default EventsPage;
